import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { inject } from '@angular/core';
import { CustomerMocksService } from '../services/mocks/customer-mocks.service';
import { CustomerSearchDto } from '@app/core/models/customer.interface';
import { VehicleMocksService } from '@app/core/services/mocks/vehicle-mocks.service';
import { JobMocksService } from '@app/core/services/mocks/jobs-mocks.service';
import { JobKitMocksService } from '@app/core/services/mocks/job-kit-mocks.service';
import { LabourCategoryMocksService } from '@app/core/services/mocks/labour-category-mocks.service';
import { QuoteMocksService } from '@app/core/services/mocks/quote-mocks.service';
import { CustomerAccountMocksService } from '@app/core/services/mocks/customer-account-mocks.service';
import { DashboardMocksService } from '@app/core/services/mocks/dashboard-mocks.service';
import { ServiceDuesMocksService } from '@app/core/services/mocks/service-dues-mocks.service';
import { AdminTenantMocksService } from '@app/core/services/mocks/admin-tenant-mocks.service';
import { AdminTenantUserMocksService } from '@app/core/services/mocks/admin-tenant-user-mocks.service';
import { AdminSystemAdminMocksService } from '@app/core/services/mocks/admin-system-admin-mocks.service';
import { OverdueInvoicesMocksService } from '@app/core/services/mocks/overdue-invoices-mocks.service';
import { NotificationUsageMocksService } from '@app/core/services/mocks/notification-usage-mocks.service';
import { NotificationHistoryMocksService } from '@app/core/services/mocks/notification-history-mocks.service';
import { VehicleSmsReminderMocksService } from '@app/core/services/mocks/vehicle-sms-reminder-mocks.service';
import { TrialRequestMocksService } from '@app/core/services/mocks/trial-request-mocks.service';
import { BillingMocksService } from '@app/core/services/mocks/billing-mocks.service';

type QueryParams = CustomerSearchDto;

export const mockApiInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  const mockCustomerService = inject(CustomerMocksService);
  const mockVehicleService = inject(VehicleMocksService);
  const mockJobsService = inject(JobMocksService);
  const mockJobKitService = inject(JobKitMocksService);
  const mockLabourCategoryService = inject(LabourCategoryMocksService);
  const mockQuoteService = inject(QuoteMocksService);
  const mockCustomerAccountService = inject(CustomerAccountMocksService);
  const mockDashboardService = inject(DashboardMocksService);
  const mockServiceDuesService = inject(ServiceDuesMocksService);
  const mockAdminTenantService = inject(AdminTenantMocksService);
  const mockAdminTenantUserService = inject(AdminTenantUserMocksService);
  const mockAdminSystemAdminService = inject(AdminSystemAdminMocksService);
  const mockOverdueInvoicesService = inject(OverdueInvoicesMocksService);
  const mockNotificationUsageService = inject(NotificationUsageMocksService);
  const mockNotificationHistoryService = inject(NotificationHistoryMocksService);
  const mockVehicleSmsReminderService = inject(VehicleSmsReminderMocksService);
  const mockBillingService = inject(BillingMocksService);
  const mockTrialRequestService = inject(TrialRequestMocksService);

  const shouldMock = (req: HttpRequest<any>) => {
    return environment.useMockData && req.url.startsWith(environment.apiUrl);
  };

  const handleMockRequest = (req: HttpRequest<any>, next: HttpHandlerFn) => {
    // The staff view of billing must be matched before the workshop's '/billing' below, which would swallow it.
    if (req.url.includes('/admin/tenants/') && req.url.endsWith('/billing')) {
      return mockBillingService.handleAdminRequest(req);
    } else if (
      req.url.endsWith('/billing') ||
      req.url.endsWith('/billing/management-link') ||
      req.url.endsWith('/billing/pay-now')
    ) {
      return mockBillingService.handleMockRequest(req);
    } else if (req.url.includes('/sms-reminder')) {
      // Must precede the generic '/vehicles' branch below or the vehicles mock swallows it.
      return mockVehicleSmsReminderService.handleMockRequest(req);
    } else if (req.url.includes('/notifications/sms-usage')) {
      return mockNotificationUsageService.handleMockRequest(req);
    } else if (req.url.includes('/notifications/history')) {
      return mockNotificationHistoryService.handleMockRequest(req);
    } else if (
      req.url.includes('/invoices/overdue') ||
      req.url.includes('/deliveries/register-bulk')
    ) {
      return mockOverdueInvoicesService.handleMockRequest(req);
    } else if (req.url.includes('/admin/signups')) {
      return mockTrialRequestService.handleMockRequest(req);
    } else if (req.url.includes('/admin/system-admins')) {
      return mockAdminSystemAdminService.handleMockRequest(req);
    } else if (req.url.includes('/admin/tenants') && req.url.includes('/users')) {
      return mockAdminTenantUserService.handleMockRequest(req);
    } else if (req.url.includes('/admin/tenants')) {
      return mockAdminTenantService.handleMockRequest(req);
    } else if (req.url.includes('/dashboard')) {
      return mockDashboardService.handleMockRequest(req);
    } else if (req.url.includes('/customers/debtors') || req.url.includes('/account/')) {
      return mockCustomerAccountService.handleMockRequest(req);
    } else if (req.url.includes('/customers')) {
      return mockCustomerService.handleMockRequest(req);
    } else if (req.url.includes('/service-dues')) {
      return mockServiceDuesService.handleMockRequest(req);
    } else if (req.url.includes('/vehicles')) {
      return mockVehicleService.handleMockRequest(req);
    } else if (req.url.includes('/jobs')) {
      return mockJobsService.handleMockRequest(req);
    } else if (req.url.includes('/labour-categories')) {
      return mockLabourCategoryService.handleMockRequest(req);
    } else if (req.url.includes('/quotes')) {
      return mockQuoteService.handleMockRequest(req);
    } else if (req.url.includes('/job-kits')) {
      return mockJobKitService.handleMockRequest(req);
    }
    return next(req);
  };

  if (shouldMock(req)) {
    return handleMockRequest(req, next);
  } else {
    return next(req);
  }
};

export const parametersFromRequest = (req: HttpRequest<any>): QueryParams => {
  const url = new URL(req.url);
  const params = url.searchParams;
  const limit = parseInt(params.get('limit') || '10', 10);
  const page = parseInt(params.get('page') || '1', 10);
  const query = params.get('q') || '';
  const sortField = params.get('sortField') || 'name';
  const sortOrder = params.get('sortOrder') || 'asc';

  return { limit, page, query, sortOrder, sortField };
};

export const parametersFromPath = (req: HttpRequest<any>, resource: string) => {
  const pathSegments = req.url.split('/');
  const tenantIdIndex = pathSegments.indexOf('v1') + 1;
  const tenantId = parseInt(pathSegments[tenantIdIndex] || '1', 10);

  // Extract customer ID if present (for endpoints like /customers/{id})
  const resourceIndex = pathSegments.indexOf(resource);
  const resourceId =
    resourceIndex !== -1 && pathSegments[resourceIndex + 1]
      ? parseInt(pathSegments[resourceIndex + 1], 10)
      : undefined;
  return { tenantId, resourceId };
};
